import React from 'react';
import { motion } from 'framer-motion';
import './SkeletonLoader.css';

/**
 * Skeleton Loader Component
 * Shows animated placeholder content while loading
 */
export function SkeletonLoader({ width = '100%', height = '1rem', className = '', style = {} }) {
  return (
    <div
      className={`skeleton-loader ${className}`}
      style={{ width, height, ...style }}
    />
  );
}

/**
 * Skeleton for sidebar chat list items
 */
export function SidebarChatSkeleton() {
  return (
    <div className="sidebar-chat-skeleton">
      <SkeletonLoader width="80%" height="1rem" style={{ marginBottom: '0.5rem' }} />
      <SkeletonLoader width="60%" height="0.75rem" />
    </div>
  );
}

/**
 * Skeleton for response cards
 */
export function ResponseCardSkeleton() {
  return (
    <div className="response-card-skeleton">
      <SkeletonLoader width="60%" height="1.5rem" style={{ marginBottom: '1rem', borderRadius: '0.25rem' }} />
      <SkeletonLoader width="100%" height="8rem" style={{ marginBottom: '0.75rem', borderRadius: '0.5rem' }} />
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <SkeletonLoader width="100px" height="2rem" style={{ borderRadius: '0.5rem' }} />
        <SkeletonLoader width="2rem" height="2rem" style={{ borderRadius: '0.5rem' }} />
      </div>
    </div>
  );
}

/**
 * Skeleton for tab content
 */
export function TabContentSkeleton() {
  return (
    <div className="tab-content-skeleton">
      <SkeletonLoader width="100%" height="6rem" style={{ marginBottom: '1rem', borderRadius: '0.5rem' }} />
      <SkeletonLoader width="40%" height="1rem" style={{ marginBottom: '0.5rem' }} />
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <SkeletonLoader width="80px" height="2rem" style={{ borderRadius: '0.5rem' }} />
        <SkeletonLoader width="80px" height="2rem" style={{ borderRadius: '0.5rem' }} />
        <SkeletonLoader width="80px" height="2rem" style={{ borderRadius: '0.5rem' }} />
        <SkeletonLoader width="80px" height="2rem" style={{ borderRadius: '0.5rem' }} />
      </div>
      <SkeletonLoader width="100%" height="3rem" style={{ borderRadius: '0.5rem' }} />
    </div>
  );
}

/**
 * Skeleton for header
 */
export function HeaderSkeleton() {
  return (
    <div className="header-skeleton" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
      <SkeletonLoader width="150px" height="2rem" />
      <div style={{ display: 'flex', gap: '1rem' }}>
        <SkeletonLoader width="80px" height="1.5rem" />
        <SkeletonLoader width="80px" height="1.5rem" />
      </div>
    </div>
  );
}

/**
 * Skeleton for query input
 */
export function QueryInputSkeleton() {
  return (
    <div className="query-input-skeleton" style={{ marginBottom: '0.5rem' }}>
      <SkeletonLoader width="100%" height="5rem" style={{ marginBottom: '0.75rem', borderRadius: '0.5rem' }} />
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <SkeletonLoader width="2.5rem" height="2.5rem" style={{ borderRadius: '0.5rem' }} />
        <SkeletonLoader width="120px" height="2.5rem" style={{ borderRadius: '0.5rem' }} />
      </div>
    </div>
  );
}

/**
 * Skeleton for model buttons
 */
export function ModelButtonsSkeleton() {
  return (
    <div className="model-buttons-skeleton" style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
      <SkeletonLoader width="100px" height="2.5rem" style={{ borderRadius: '0.5rem' }} />
      <SkeletonLoader width="100px" height="2.5rem" style={{ borderRadius: '0.5rem' }} />
      <SkeletonLoader width="100px" height="2.5rem" style={{ borderRadius: '0.5rem' }} />
      <SkeletonLoader width="100px" height="2.5rem" style={{ borderRadius: '0.5rem' }} />
    </div>
  );
}

/**
 * Skeleton for footer
 */
export function FooterSkeleton() {
  return (
    <div className="footer-skeleton" style={{ textAlign: 'center', padding: '1.5rem 0' }}>
      <SkeletonLoader width="200px" height="1.5rem" style={{ margin: '0 auto 0.5rem auto' }} />
      <SkeletonLoader width="300px" height="1rem" style={{ margin: '0 auto 0.5rem auto' }} />
      <SkeletonLoader width="250px" height="0.875rem" style={{ margin: '0 auto 0.5rem auto' }} />
      <SkeletonLoader width="400px" height="0.75rem" style={{ margin: '0 auto 0.5rem auto' }} />
      <SkeletonLoader width="150px" height="0.75rem" style={{ margin: '0 auto' }} />
    </div>
  );
}

